import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError } from '../../common/api'
import { driversTypeSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { DriversType } from './model'

// Fetch

const fetchDriversTypesEpic = (action$, store) => {
    const tableIdentifier = 'drivers_types_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: 'drivers-types/list',
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(driversTypeSchema)
                        )

                        const driversTypes = normalizedEntitiesToRecordMap(normalizedData.entities.drivers_types, DriversType, normalizedData.result)

                        const observables = [Observable.of(actionCreators.fetchDriversTypesFulfilled(driversTypes))]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriversTypesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchDriversTypeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().driversTypes.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `drivers-types/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driversTypeSchema)

                        const driversTypes = normalizedEntitiesToRecordMap(normalizedData.entities.drivers_types, DriversType)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchDriversTypesFulfilled(driversTypes)),
                            Observable.of(actionCreators.fetchDriversTypeFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriversTypesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveDriversTypeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().driversTypes.getIn(['current', 'saving']))
        .switchMap(action => {
            // eslint-disable-next-line prefer-object-spread
            const values = Object.assign({}, action.payload)

            let path = 'drivers-types'
            let method = 'POST'

            if (values.id) {
                path = `drivers-types/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driversTypeSchema)

                        const driversTypes = normalizedEntitiesToRecordMap(normalizedData.entities.drivers_types, DriversType)
                        const driversType = driversTypes.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveDriversTypeFulfilled(driversType)))
                    })
                    .catch(error => Observable.of(actionCreators.saveDriversTypeRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteDriversTypeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().driversTypes.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `drivers-types/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteDriversTypeFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteDriversTypeRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

export default [fetchDriversTypesEpic, fetchDriversTypeEpic, saveDriversTypeEpic, deleteDriversTypeEpic]
